import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { Route } from 'react-router-dom';
import { connect } from 'react-redux';
import { get } from 'lodash';
import classnames from 'classnames';

import { hyphenateUrlComponents, generateSearchPath } from '../../utils/urlHelpers/boats';
import { generateSearchPath as generateEngineSearchPath  } from '../../utils/urlHelpers/engines';
import Dropdown from '../Dropdown';
import { OptionSet, Option } from '../OptionSet';
import Input from '../Input';
import messages from '../../utils/translationMessages';
import { getFacets } from '../../store/actions';
import { setHomeSearchTracking } from '../../store/actions/dataLayer';
import { bindActionCreators } from 'redux';
import { emptySubFacet, getRegions } from '../../utils/multiFacetHelper';
import { PortalConfigContext } from '../../config/portal';
import { getLength as getUomAbbr } from '../../utils/uomHelper.js';
import './styles.css';

class QuickSearch extends PureComponent {
  state = {
    filterCategory: 'power',
    categories: [],
    category: [],
    location: [],
    makeModel: [],
    minPrice: '',
    maxPrice: '',
    length: [],
    minLength: '',
    maxLength: '',
    minYear: ''
  }

  componentDidMount() {
    this.setState({
      categories: this.filterCategories(this.state.filterCategory)
    });
  }

  /** This effect triggers each time one of "filterCategory or category"
   * changes but only one time if both changes at the same time */
  componentDidUpdate(prevProps, prevState) {
    if (prevState.category !== this.state.category || prevState.filterCategory !== this.state.filterCategory) {
      this.props.getFacets(generateSearchPath({ multiFacetedBoatTypeClass: this.getClass(this.state.category) }));
    }
    /** use memo to avoid calling filter categories for each render
   * and only call it when facets or filter changes */
    if (prevProps.facets !== this.props.facets || prevState.filterCategory !== this.state.filterCategory) {
      this.setState({
        categories: this.filterCategories(this.state.filterCategory)
      });
    }
  }

  /** Filter class by selected category
   * @param {string} filter
   */
  filterCategories = (filter) => {
    const { facets, intl: { formatMessage: t } } = this.props;
    let categories = get(facets, 'class', []).reduce((accumulator, element) => {
      if (get(this.context.boatTypes, filter, []).includes(element.value)) {
        accumulator.push({ ...element, label: t(messages.classFacetValues[element.value]) });
      }
      return accumulator;
    }, []);
    return categories.sort((a, b) => a.label.localeCompare(b.label));
  };

  /** Build make payload for generateSearchPath */
  getMakeModel = () => {
    if (this.state.makeModel.find(e => e.default)) {
      return {};
    }
    return this.state.makeModel.reduce((selectedMakeModels, item) =>
      emptySubFacet(hyphenateUrlComponents(item.value), selectedMakeModels), {});
  };

  /** Build class payload for generateSearchPath
   * also used to request filtered facets
   * @param {array} categories
  */
  getClass = (categories) => {
    if (categories.find(e => e.default)) {
      return {};
    }
    return {
      [this.state.filterCategory]: categories.map((item) => item.value)
    };
  };

  /** Build Search */
  getParams = () => {
    let params = {
      makeModel: this.getMakeModel(),
      multiFacetedBoatTypeClass: this.getClass(this.state.category),
      price: {},
      length: {},
      year: {}
    };
    if (this.state.maxPrice) {
      params.price.max = this.state.maxPrice;
    }
    if (this.state.location.length && !this.state.location[0].default) {
      params.region = this.state.location[0].value;
    }
    if (this.state.length.length !== 0) {
      params.length.min = this.state.length[0].min;
      params.length.max = this.state.length[0].max;
    }
    if (this.state.minYear) {
      params.year.min = this.state.minYear;
    }
    return params;
  }
  searchPath = () => {
    if (this.state.filterCategory === 'engines') {
      return generateEngineSearchPath(this.getParams());
    }

    return generateSearchPath(this.getParams());
  };

  /** Reset the selected values of category and make each time filter is updated
   * @param {string} newFilter
  */
  onFilterChange = (newFilter) => {
    this.setState({
      category: [],
      makeModel: [],
      filterCategory: newFilter,
    });
  };

  render() {
    const { facets, intl: { formatMessage: t } } = this.props;
    const { filterCategory } = this.state;
    const lengthAbbr = getUomAbbr();
    const categories = [
      { value: 'all-class', default: true, label: t(messages.classFacetValues['all-class']) },
      ...this.state.categories
    ];
    const makes = [
      { value: t(messages.allMake), default: true },
      ...get(facets, 'make', []).sort((a, b) => a.value.localeCompare(b.value))
    ];
    const lengths = [
      { value: `0 - 5${lengthAbbr}.`, min: 0, max: 5},
      { value: `5 - 10${lengthAbbr}.`, min: 5, max: 10},
      { value: `10 - 15${lengthAbbr}.`, min: 10, max: 15 },
      { value: `15 - 20${lengthAbbr}.`, min: 15, max: 20 },
      { value: `20 - 25${lengthAbbr}.`, min: 20, max: 25 },
      { value: `25 - 30${lengthAbbr}.`, min: 25, max: 30 },
      { value: `30${lengthAbbr}.+`, min: 30 },
    ];

    const regions = [
      { value: 'all-regions', default: true },
      ...getRegions(facets, this.context.country).sort((a, b) => a.value.localeCompare(b.value))
    ];

    const hideLocationInput = get(this.context, 'pages.home.hidden.quicksearch.location', false);
    const hideMakeInput = get(this.context, 'pages.home.hidden.quicksearch.make', false);
    const hideCategoryInput = get(this.context, 'pages.home.hidden.quicksearch.category', false);
    const hideMinYearInput = get(this.context, 'pages.home.hidden.quicksearch.minYear', false);
    const hideLengthInput = get(this.context, 'pages.home.hidden.quicksearch.length', false);
    const hideUnpoweredCategory = get(this.context, 'pages.home.hidden.quicksearch.unpoweredCategory', false);
    const hideEngineCategory = get(this.context, 'pages.home.hidden.quicksearch.engineCategory', false);
    const bottomRowClasses = classnames('bottom-row', { 'bottom-row__no-location': hideLocationInput });

    return (
      <Route render={() => (
        <div className="quick-search-container">
          <div className="quick-search">
            <h1>{t(messages.quickSearch.title)}</h1>
            <div className="top-row">
              <div className="quick-search-category-selectors-container">
                <label htmlFor="power">
                  <input
                    id="power"
                    type="radio"
                    checked={filterCategory === 'power'}
                    value="power"
                    name="category"
                    onChange={() => this.onFilterChange('power')}/>
                  {t(messages.searchCategory.motorBoats)}
                </label>

                <label htmlFor="sail">
                  <input
                    id="sail"
                    type="radio"
                    checked={filterCategory === 'sail'}
                    value="sail"
                    name="category"
                    onChange={() => this.onFilterChange('sail')}/>
                  {t(messages.searchCategory.sailboats)}
                </label>

                { !hideUnpoweredCategory && <label htmlFor="unpowered">
                  <input
                    id="unpowered"
                    type="radio"
                    checked={filterCategory === 'unpowered'}
                    value="unpowered"
                    name="category"
                    onChange={() => this.onFilterChange('unpowered')}/>
                  {t(messages.searchCategory.unpowered)}
                </label> }

                { !hideEngineCategory && <label htmlFor="engines">
                  <input
                    id="engines"
                    type="radio"
                    checked={filterCategory === 'engines'}
                    value="engines"
                    name="category"
                    onChange={() => this.onFilterChange('engines')}/>
                  {t(messages.searchCategory.engines)}
                </label>}

              </div>
            </div>
            <form className={bottomRowClasses} action={this.searchPath()} autoComplete="off">
              { !hideCategoryInput && <Dropdown
                label={t(messages.category)}
                filter
                setValue={(value) => this.setState({ category: value, makeModel: [], location: [] })}
                value={this.state.category}
                options={categories}
                optionKey="label"
                render={
                  (options, inheritProps) => (
                    <OptionSet {...inheritProps}>
                      {options.map((option, idx) => (
                        <Option key={'category-' + idx} option={option} {...inheritProps}>
                          {option.label}
                        </Option>
                      ))}
                    </OptionSet>
                  )
                }
              /> }
              { !hideMakeInput &&
                <Dropdown
                  label={t(messages.make)}
                  filter
                  setValue={(value) => this.setState({ makeModel: value, location: [] })}
                  value={this.state.makeModel}
                  options={makes}
                  render={
                    (options, inheritProps) => (
                      <OptionSet {...inheritProps}>
                        {options.map((option, idx) => (
                          <Option key={'make-' + idx} option={option} {...inheritProps}>
                            {option.value}
                          </Option>
                        ))}
                      </OptionSet>
                    )
                  }
                />
              }

              { this.state.filterCategory !== 'engines' && !hideLengthInput && <Dropdown
                label={t(messages.length)}
                setValue={(value) => this.setState({ length: value })}
                value={this.state.length}
                options={lengths}
                className="input-length"
                render={
                  (options, inheritProps) => (
                    <OptionSet {...inheritProps}>
                      { options.map((option) => (
                        <Option key={option.min + '-' + option.max} option={option} {...inheritProps} >
                          {option.value}
                        </Option>
                      ))}
                    </OptionSet>
                  )
                }
              /> }

              {!hideMinYearInput &&  <Input
                label={t(messages.yearMin)}
                setValue={(value) => this.setState({ minYear: value })}
                value={this.state.minYear}
                min={1850}
                type={'number'}
              /> }
              <Input
                label={t(messages.priceMax)}
                setValue={(value) => this.setState({ maxPrice: value })}
                value={this.state.maxPrice}
                min={1000}
                type={'number'}
              />
              { !hideLocationInput &&
                <Dropdown
                  className="location"
                  label={t(messages.location)}
                  filter
                  setValue={(value) => this.setState({ location: value })}
                  value={this.state.location}
                  options={regions}
                  translateOption={options => t(messages.countryRegions[options[0].value]) }
                  render={
                    (options, inheritProps) => (
                      <OptionSet {...inheritProps}>
                        {options.map((option, idx) => (
                          <Option key={'region-' + idx} {...inheritProps} option={option}>
                            {t(messages.countryRegions[option.value])}
                          </Option>
                        ))}
                      </OptionSet>
                    )
                  }
                >
                </Dropdown>
              }
              <button type="submit"
                className="button-search"
                onClick={() => this.props.setHomeSearchTracking(this.getParams())} >
                {t(messages.searchTitle)}
              </button>
            </form>
          </div>
        </div>
      )} />
    );
  }
}
QuickSearch.contextType = PortalConfigContext;
QuickSearch.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  facets: PropTypes.object,
  getFacets: PropTypes.func.isRequired,
  setHomeSearchTracking: PropTypes.func.isRequired
};
const mapStateToProps = state => {
  return {
    facets: get(state.app, 'data.facets', {}),
  };
};
export default connect(
  mapStateToProps,
  dispatch => bindActionCreators({
    getFacets,
    setHomeSearchTracking,
  }, dispatch)
)(injectIntl(QuickSearch));
